import type { MetadataEvent } from '../../../../types'
import { useActiveChatId, useIsTurnLive, useStreamingMetadataForChat } from '../../../../store'

export type TurnActivity = {
  /** Whether this chat's turn is live (loading, streaming, or running tools). */
  isTurnActive: boolean
  streamingMetadata: MetadataEvent | undefined
}

/**
 * A group's live turn state, gated by its position. This is what lets every
 * group render the same container type: instead of swapping in a
 * streaming-subscribed component for the most-recent group (destroying the
 * subtree — and its expanded tool lists and mid-reveal state — when the group
 * stops being most recent), position only changes what this hook reads.
 *
 * Only the most-recent group (the streaming turn) and the last user group
 * (staged action preview) read live turn state — and per chat via
 * `useIsTurnLive(chatId)`, the render tree's one seam onto the engine's
 * per-chat `TurnStatus` (HIG-5965), never a global boolean that stays true
 * while ANY chat streams (HIG-5962). An idle chat therefore never renders
 * another chat's active turn, and history rows read inert constants (their
 * hooks are passed `undefined` and never signal a change).
 *
 * The reads key on the group's own chat (its primary message's conversation),
 * so this subsumes the interim R9 active-chat-match gate: both `useIsTurnLive`
 * and `useStreamingMetadataForChat` resolve the temp→real id swap internally,
 * and a background continuation that reassigns `activeChatId` out from under
 * an open view can't make this view's groups render another chat's turn.
 */
export function useTurnActivity(opts: {
  isMostRecent: boolean
  isLastUserGroup: boolean
  conversationId: string | undefined
}): TurnActivity {
  const { isMostRecent, isLastUserGroup, conversationId } = opts
  const isLive = isMostRecent || isLastUserGroup
  const activeChatId = useActiveChatId()
  // Identity-less groups (fixtures, freshly minted optimistic rows) belong to
  // the list that rendered them, which is the active chat's.
  const gatedChatId = isLive ? (conversationId ?? activeChatId) : undefined
  const isTurnActive = useIsTurnLive(gatedChatId)
  const streamingMetadata = useStreamingMetadataForChat(gatedChatId)

  return { isTurnActive, streamingMetadata }
}
